'use client'

import { Menu as BaseMenu } from '@base-ui/react/menu'

import { cn } from '@/core/utils/common'

import type { ReactNode } from 'react'

type MenuItemAction = {
  id: string
  label: string
  onClick: () => void
  icon?: ReactNode
  disabled?: boolean
  danger?: boolean
}

type MenuDivider = {
  id: string
  type: 'divider'
}

type DropdownMenuItem = MenuItemAction | MenuDivider

function isDivider(item: DropdownMenuItem): item is MenuDivider {
  return 'type' in item && item.type === 'divider'
}

type DropdownMenuProps = {
  trigger: ReactNode
  items: DropdownMenuItem[]
  align?: 'start' | 'end'
  className?: string
}

export default function DropdownMenu({
  trigger,
  items,
  align = 'start',
  className,
}: DropdownMenuProps) {
  return (
    <BaseMenu.Root modal={false}>
      <BaseMenu.Trigger className="cursor-pointer rounded-control outline-none focus-visible:bg-surface-strong">
        {trigger}
      </BaseMenu.Trigger>
      <BaseMenu.Portal>
        <BaseMenu.Positioner
          align={align}
          sideOffset={8}
          className="z-[5000200]"
        >
          <BaseMenu.Popup
            className={cn(
              'z-50 min-w-40 origin-[var(--transform-origin)] rounded-panel',
              'glass-menu p-1.5 outline-none',
              'focus-visible:border-point',
              'transition-[opacity,transform] duration-150',
              'data-[starting-style]:scale-[0.98] data-[starting-style]:opacity-0',
              'data-[ending-style]:scale-[0.98] data-[ending-style]:opacity-0',
              className
            )}
          >
            {items.map((item) => {
              if (isDivider(item)) {
                return (
                  <BaseMenu.Separator
                    key={item.id}
                    className="my-1 border-t border-foreground/[0.08]"
                  />
                )
              }

              return (
                <BaseMenu.Item
                  key={item.id}
                  label={item.label}
                  disabled={item.disabled}
                  onClick={item.onClick}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-control px-3 py-2.5 text-left text-c14 outline-none',
                    'transition-colors data-[highlighted]:bg-point/10',
                    item.danger
                      ? 'text-error hover:bg-error/10'
                      : 'hover:bg-point/10',
                    item.disabled
                      ? 'cursor-not-allowed opacity-50'
                      : 'cursor-pointer'
                  )}
                >
                  {item.icon && (
                    <span className="size-4 shrink-0">{item.icon}</span>
                  )}
                  {item.label}
                </BaseMenu.Item>
              )
            })}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  )
}
